import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Dialog from './TivigoDialog';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ShieldRoundedIcon from '@mui/icons-material/GppMaybeRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import TivigoToast from './TivigoToast';
import {
  hasInvalidCredentialCharacters,
  isCredentialValueAllowed,
  PASSWORD_CHARACTER_ERROR,
} from './utils/credentialPolicy';
import './userPanelAccountModal.css';

const TABS = [
  { id: 'personal', label: 'Kişisel' },
  { id: 'password', label: 'Şifre' },
  { id: 'email', label: 'E-posta' },
  { id: 'phone', label: 'Telefon' },
  { id: 'devices', label: 'Oturum Kayıtları' },
];

const TAB_INDICATOR_METRICS = [
  { x: '0px', width: 'calc(18% - 5.04px)' },
  { x: 'calc(18% - 0.04px)', width: 'calc(18% - 5.04px)' },
  { x: 'calc(36% - 0.08px)', width: 'calc(18% - 5.04px)' },
  { x: 'calc(54% - 0.12px)', width: 'calc(18% - 5.04px)' },
  { x: 'calc(72% - 0.16px)', width: 'calc(28% - 7.84px)' },
];

const TEAMS = [
  { value: '', label: 'Takım seçin' },
  { value: 'galatasaray', label: 'Galatasaray' },
  { value: 'fenerbahçe', label: 'Fenerbahçe' },
  { value: 'beşiktaş', label: 'Beşiktaş' },
  { value: 'trabzonspor', label: 'Trabzonspor' },
  { value: 'başakşehir', label: 'Başakşehir' },
  { value: 'adana demirspor', label: 'Adana Demirspor' },
  { value: 'alanyaspor', label: 'Alanyaspor' },
  { value: 'antalyaspor', label: 'Antalyaspor' },
  { value: 'bodrum fk', label: 'Bodrum FK' },
  { value: 'çaykur rizespor', label: 'Çaykur Rizespor' },
  { value: 'gaziantep fk', label: 'Gaziantep FK' },
  { value: 'göztepe', label: 'Göztepe' },
  { value: 'hatayspor', label: 'Hatayspor' },
  { value: 'kasımpaşa', label: 'Kasımpaşa' },
  { value: 'kayserispor', label: 'Kayserispor' },
  { value: 'konyaspor', label: 'Konyaspor' },
  { value: 'samsunspor', label: 'Samsunspor' },
  { value: 'sivasspor', label: 'Sivasspor' },
  { value: 'diğer', label: 'Diğer' },
];

const DEMO_DEVICE = {
  id: 'demo-windows',
  device_name: 'Windows / Chrome',
  ip_address: '192.168.1.24',
  trusted_at: new Date().toISOString(),
  expires_at: Math.floor(Date.now() / 1000) + (30 * 86400),
};

const DEMO_LOGINS = [
  { id: 2, login_at: new Date().toISOString(), ip_address: '212.253.166.20', device: 'Chrome · Windows', location: 'Türkiye, Adana', success: true },
  { id: 1, login_at: new Date(Date.now() - 86400000).toISOString(), ip_address: '85.105.12.7', device: 'Tivigo Uygulaması · Android', location: 'Türkiye, İstanbul', success: false, failure_reason: 'Hatalı şifre' },
];

const resolveTab = (value) => TABS.some((tab) => tab.id === value) ? value : 'personal';

const getAccountProfile = (userInfo, usernameOverride, previewMode = false) => {
  const info = userInfo?.user_info || {};
  const user = userInfo?.user || {};

  return {
    username: usernameOverride || info.username || user.username || userInfo?.username || (previewMode ? 'demo_user' : 'Tivigo kullanıcısı'),
    email: user.email || info.email || userInfo?.email || (previewMode ? 'demo@tivigo.test' : ''),
    phone: user.phone || info.phone || userInfo?.phone || (previewMode ? '0555 555 55 55' : ''),
    name: user.ad || user.name || info.ad || info.name || userInfo?.ad || (previewMode ? 'Demo Kullanıcı' : ''),
    team: user.takim || user.team || info.takim || info.team || userInfo?.takim || '',
  };
};

const normalizeLoadedProfile = (result, fallback) => {
  if (result?.success === false) throw new Error(result.message || 'Hesap bilgileri alınamadı.');
  const source = result?.data?.user || result?.user || result?.data || result || {};
  return {
    username: source.username || fallback.username,
    email: source.email ?? fallback.email,
    phone: source.phone ?? fallback.phone,
    name: source.ad ?? source.name ?? fallback.name,
    team: source.takim ?? source.team ?? fallback.team,
  };
};

const normalizeDevices = (result) => {
  if (result?.success === false) throw new Error(result.message || 'Güvenilir cihazlar alınamadı.');
  const devices = result?.data?.devices || result?.devices || result?.data || result || [];
  return Array.isArray(devices) ? devices : [];
};

const normalizeLogins = (result) => {
  if (result?.success === false) throw new Error(result.message || 'Oturum kayıtları alınamadı.');
  const logins = result?.data?.logins || result?.logins || result?.data || result || [];
  return Array.isArray(logins) ? logins : [];
};

const ensureSuccessfulResult = (result, fallbackMessage) => {
  if (result?.success === false) throw new Error(result.message || fallbackMessage);
  return result;
};

const capitalizeName = (value) => value
  .trim()
  .split(/\s+/)
  .map((word) => `${word.charAt(0).toLocaleUpperCase('tr-TR')}${word.slice(1).toLocaleLowerCase('tr-TR')}`)
  .join(' ');

const maskEmail = (value) => {
  if (!value || !value.includes('@')) return '***@***.com';
  const [local, domain] = value.split('@');
  const domainParts = domain.split('.');
  const extension = domainParts.pop() || '';
  return `${local.slice(0, 2)}${'*'.repeat(Math.max(3, local.length - 2))}@${'*'.repeat(Math.max(3, domainParts.join('.').length))}.${extension.slice(-2)}`;
};

const maskPhone = (value) => {
  const text = String(value || '');
  if (text.length < 2) return '***-***-**';
  return `${'*'.repeat(Math.max(3, text.length - 2))}${text.slice(-2)}`;
};

const normalizePhone = (value) => String(value || '').replace(/\D/g, '');

const parseFlexibleDate = (value) => {
  if (value == null || value === '') return null;
  const numeric = Number(value);
  const date = Number.isFinite(numeric)
    ? new Date(numeric > 100000000000 ? numeric : numeric * 1000)
    : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value, includeTime = false, includeSeconds = false) => {
  const date = parseFlexibleDate(value);
  if (!date) return 'Bilgi yok';
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...(includeTime ? { hour: '2-digit', minute: '2-digit' } : {}),
    // Oturum kayitlarinda ard arda girisler ayni dakikaya dusuyor; saniye ayirt ediyor.
    ...(includeTime && includeSeconds ? { second: '2-digit' } : {}),
  }).format(date);
};

const passwordSecurity = {
  hasSequentialNumbers(password) {
    for (let index = 0; index <= password.length - 3; index += 1) {
      const part = password.slice(index, index + 3);
      if (/^\d{3}$/.test(part)) {
        const digits = part.split('').map(Number);
        if (digits[1] === digits[0] + 1 && digits[2] === digits[1] + 1) return true;
      }
    }
    return false;
  },
  hasRepeatingChars: (password) => /(.)\1{2,}/.test(password),
  isCommonPassword(password) {
    return [
      '123456', '123456789', 'qwerty', 'password', '12345678', 'abc123',
      '123123', 'password123', '1234567890', 'qwerty123', '000000',
      'iloveyou', '1qaz2wsx', 'admin', 'welcome', 'monkey', 'login',
      'abc123456', 'master', '123321', 'welcome123', 'solo', 'pass',
      '123qwe', 'zxcvbnm', 'football', 'baseball', 'dragon',
    ].includes(password.toLocaleLowerCase('tr-TR'));
  },
  calculate(password) {
    const checks = {
      length: password.length >= 8,
      numbers: /\d/.test(password),
      letters: /[a-zA-Z]/.test(password),
      long: password.length >= 12,
      noSequential: !this.hasSequentialNumbers(password),
      noRepeating: !this.hasRepeatingChars(password),
      notCommon: !this.isCommonPassword(password),
    };
    const score = Math.min(100,
      (checks.length ? 30 : 0)
      + (checks.numbers ? 20 : 0)
      + (checks.letters ? 15 : 0)
      + (checks.long ? 10 : 0)
      + (checks.noSequential ? 15 : 0)
      + (checks.noRepeating ? 5 : 0)
      + (checks.notCommon ? 5 : 0));
    const level = score < 25 ? 'Çok zayıf' : score < 40 ? 'Zayıf' : score < 60 ? 'Orta' : score < 80 ? 'Güçlü' : 'Çok güçlü';
    return { checks, score, level };
  },
};

function UserPanelAccountModal({
  open,
  onClose,
  userInfo,
  username,
  initialTab = 'personal',
  previewMode = false,
  onLoadAccount,
  onUpdateProfile,
  onChangePassword,
  onUpdateEmail,
  onSendEmailCode,
  onUpdatePhone,
  onSendPhoneCode,
  onLoadTrustedDevices,
  onRemoveTrustedDevice,
  onLoadLoginHistory,
  onReportLogin,
  onPasswordChanged,
  onUpdated,
}) {
  const baseProfile = useMemo(
    () => getAccountProfile(userInfo, username, previewMode),
    [previewMode, userInfo, username],
  );
  const [activeTab, setActiveTab] = useState(resolveTab(initialTab));
  const [profile, setProfile] = useState(baseProfile);
  const [personalForm, setPersonalForm] = useState({ name: baseProfile.name, team: baseProfile.team });
  const [teamSelectOpen, setTeamSelectOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ email: '', currentPassword: '', newPassword: '' });
  // E-posta degisikligi iki adimli: once mevcut adrese 6 haneli kod gider, sonra kod dogrulanir.
  const [emailForm, setEmailForm] = useState({ newEmail: '', code: '' });
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [emailCodeSeconds, setEmailCodeSeconds] = useState(0);
  // Telefon degisikligi de iki adimli: kod hesaba kayitli e-posta adresine gider.
  const [phoneForm, setPhoneForm] = useState({ newPhone: '', code: '' });
  const [phoneCodeSent, setPhoneCodeSent] = useState(false);
  const [phoneCodeSeconds, setPhoneCodeSeconds] = useState(0);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [accountLoading, setAccountLoading] = useState(false);
  const [activeRequest, setActiveRequest] = useState('');
  const [alert, setAlert] = useState(null);
  const [trustedDevices, setTrustedDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesError, setDevicesError] = useState('');
  const [removingDeviceId, setRemovingDeviceId] = useState(null);
  const [loginHistory, setLoginHistory] = useState([]);
  const [loginsLoading, setLoginsLoading] = useState(false);
  const [loginsError, setLoginsError] = useState('');
  // "Bu ben degilim": once onay sorulur, sonra tum oturumlar kapanir.
  const [reportTarget, setReportTarget] = useState(null);
  const [reportingId, setReportingId] = useState(null);
  const [reportDone, setReportDone] = useState(false);
  const [activePanelHeight, setActivePanelHeight] = useState(null);
  const [activePanelScrollable, setActivePanelScrollable] = useState(false);
  const panelRefs = useRef({});
  const initializedOpenRef = useRef(false);
  const baseProfileRef = useRef(baseProfile);
  const userInfoRef = useRef(userInfo);

  baseProfileRef.current = baseProfile;
  userInfoRef.current = userInfo;

  const passwordStrength = useMemo(
    () => passwordSecurity.calculate(passwordForm.newPassword),
    [passwordForm.newPassword],
  );

  // Kodun gecerlilik geri sayimi; sifirlaninca yeniden kod istenebilir.
  useEffect(() => {
    if (emailCodeSeconds <= 0) return undefined;
    const timer = setTimeout(() => setEmailCodeSeconds((current) => current - 1), 1000);
    return () => clearTimeout(timer);
  }, [emailCodeSeconds]);

  useEffect(() => {
    if (phoneCodeSeconds <= 0) return undefined;
    const timer = setTimeout(() => setPhoneCodeSeconds((current) => current - 1), 1000);
    return () => clearTimeout(timer);
  }, [phoneCodeSeconds]);
  const newPasswordInvalidChar = hasInvalidCredentialCharacters(passwordForm.newPassword);
  const personalFormDirty = capitalizeName(personalForm.name) !== capitalizeName(profile.name || '')
    || String(personalForm.team || '') !== String(profile.team || '');
  const selectedTeam = TEAMS.find((team) => team.value === personalForm.team) || TEAMS[0];
  const activeTabIndex = Math.max(0, TABS.findIndex((tab) => tab.id === activeTab));
  const accountTabIndicator = TAB_INDICATOR_METRICS[activeTabIndex];
  const activeSubmitAction = ({
    personal: {
      label: 'Bilgileri Kaydet',
      pendingLabel: 'Kaydediliyor...',
      pending: activeRequest === 'personal',
      disabled: activeRequest === 'personal' || !personalForm.name.trim() || !personalFormDirty,
    },
    password: {
      label: 'Şifreyi Değiştir',
      pendingLabel: 'Değiştiriliyor...',
      pending: activeRequest === 'password',
      disabled: activeRequest === 'password'
        || !passwordForm.email
        || !passwordForm.currentPassword
        || !passwordForm.newPassword
        || newPasswordInvalidChar,
    },
    email: emailCodeSent
      ? {
        label: 'Kodu Onayla ve Değiştir',
        pendingLabel: 'Doğrulanıyor...',
        pending: activeRequest === 'email',
        disabled: activeRequest === 'email' || emailForm.code.trim().length !== 6,
      }
      : {
        label: 'Doğrulama Kodu Gönder',
        pendingLabel: 'Kod gönderiliyor...',
        pending: activeRequest === 'email-code',
        disabled: activeRequest === 'email-code' || !emailForm.newEmail.trim(),
      },
    phone: phoneCodeSent
      ? {
        label: 'Kodu Onayla ve Değiştir',
        pendingLabel: 'Doğrulanıyor...',
        pending: activeRequest === 'phone',
        disabled: activeRequest === 'phone' || phoneForm.code.trim().length !== 6,
      }
      : {
        label: 'Doğrulama Kodu Gönder',
        pendingLabel: 'Kod gönderiliyor...',
        pending: activeRequest === 'phone-code',
        disabled: activeRequest === 'phone-code' || !phoneForm.newPhone.trim(),
      },
  })[activeTab] || null;

  useEffect(() => {
    if (!open) {
      initializedOpenRef.current = false;
      return undefined;
    }
    if (initializedOpenRef.current) return undefined;

    initializedOpenRef.current = true;
    let cancelled = false;
    const openingProfile = baseProfileRef.current;
    const openingUserInfo = userInfoRef.current;

    setActiveTab(resolveTab(initialTab));
    setProfile(openingProfile);
    setPersonalForm({ name: openingProfile.name, team: openingProfile.team });
    setTeamSelectOpen(false);
    setPasswordForm({ email: '', currentPassword: '', newPassword: '' });
    setEmailForm({ newEmail: '', code: '' });
    setEmailCodeSent(false);
    setEmailCodeSeconds(0);
    setPhoneForm({ newPhone: '', code: '' });
    setPhoneCodeSent(false);
    setPhoneCodeSeconds(0);
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setActiveRequest('');
    setAlert(null);
    setTrustedDevices(previewMode ? [DEMO_DEVICE] : []);
    setDevicesError('');
    setRemovingDeviceId(null);

    if (previewMode || typeof onLoadAccount !== 'function') {
      setAccountLoading(false);
      return undefined;
    }

    setAccountLoading(true);
    Promise.resolve(onLoadAccount(openingUserInfo))
      .then((result) => {
        if (cancelled) return;
        const nextProfile = normalizeLoadedProfile(result, openingProfile);
        setProfile(nextProfile);
        setPersonalForm({ name: nextProfile.name, team: nextProfile.team });
      })
      .catch((error) => {
        if (!cancelled) setAlert({ type: 'error', message: error.message || 'Hesap bilgileri alınamadı.' });
      })
      .finally(() => {
        if (!cancelled) setAccountLoading(false);
      });

    return () => { cancelled = true; };
  }, [initialTab, onLoadAccount, open, previewMode]);

  const invokeAction = async (callback, payload, fallbackMessage) => {
    if (previewMode) return { success: true, preview: true };
    if (typeof callback === 'function') {
      return ensureSuccessfulResult(await callback(payload), fallbackMessage);
    }
    throw new Error('Bu işlem için sunucu bağlantısı henüz tanımlanmadı.');
  };

  const loadTrustedDevices = useCallback(async () => {
    setDevicesError('');
    if (previewMode) {
      setTrustedDevices([DEMO_DEVICE]);
      setDevicesLoading(false);
      return;
    }
    if (typeof onLoadTrustedDevices !== 'function') {
      setDevicesError('Güvenilir cihaz bağlantısı henüz tanımlanmadı.');
      return;
    }

    setDevicesLoading(true);
    try {
      const result = await onLoadTrustedDevices(userInfo);
      setTrustedDevices(normalizeDevices(result));
    } catch (error) {
      setDevicesError(error.message || 'Güvenilir cihazlar alınamadı.');
    } finally {
      setDevicesLoading(false);
    }
  }, [onLoadTrustedDevices, previewMode, userInfo]);

  // Oturum kayitlari: kullanicinin kendi giris gecmisi (login_history ucu).
  const loadLoginHistory = useCallback(async () => {
    setLoginsError('');
    if (previewMode) {
      setLoginHistory(DEMO_LOGINS);
      setLoginsLoading(false);
      return;
    }
    if (typeof onLoadLoginHistory !== 'function') {
      setLoginsError('Oturum kayıtları bağlantısı henüz tanımlanmadı.');
      return;
    }

    setLoginsLoading(true);
    try {
      const result = await onLoadLoginHistory(userInfo);
      setLoginHistory(normalizeLogins(result));
    } catch (error) {
      setLoginsError(error.message || 'Oturum kayıtları alınamadı.');
    } finally {
      setLoginsLoading(false);
    }
  }, [onLoadLoginHistory, previewMode, userInfo]);

  useEffect(() => {
    if (open && activeTab === 'devices') loadLoginHistory();
  }, [activeTab, loadLoginHistory, open]);

  // Girisi reddet: sunucu tum token'lari gecersiz kilar, kullanici her cihazdan duser.
  const handleReportLogin = useCallback(async () => {
    const item = reportTarget;
    if (!item) return;

    setReportingId(item.id);
    try {
      if (previewMode) {
        await new Promise((resolve) => { setTimeout(resolve, 500); });
      } else if (typeof onReportLogin === 'function') {
        await onReportLogin({ loginId: item.id, userInfo });
      } else {
        throw new Error('Bildirim bağlantısı henüz tanımlanmadı.');
      }
      setReportTarget(null);
      setReportDone(true);
      setLoginHistory((prev) => prev.map((row) => (row.id === item.id ? { ...row, reported: true } : row)));
    } catch (error) {
      setReportTarget(null);
      setAlert({ type: 'error', message: error.message || 'Bildirim gönderilemedi.' });
    } finally {
      setReportingId(null);
    }
  }, [onReportLogin, previewMode, reportTarget, userInfo]);

  useEffect(() => {
    if (!alert) return undefined;
    const timeoutId = window.setTimeout(() => setAlert(null), 3600);
    return () => window.clearTimeout(timeoutId);
  }, [alert]);

  useLayoutEffect(() => {
    if (!open || accountLoading) return undefined;
    const panel = panelRefs.current[activeTab];
    if (!panel) return undefined;

    const measurePanel = () => {
      const slide = panel.closest('.tvup-account-panel-slide');
      const modal = panel.closest('.tvup-account-modal');
      const dialogContainer = modal?.parentElement;
      const header = modal?.querySelector('.tvup-account-header');
      const tabs = modal?.querySelector('.tvup-account-edit-tabs');
      const footer = modal?.querySelector('.tvup-account-footer');
      const slideStyles = slide ? window.getComputedStyle(slide) : null;
      const containerStyles = dialogContainer ? window.getComputedStyle(dialogContainer) : null;
      const modalStyles = modal ? window.getComputedStyle(modal) : null;
      const tabsStyles = tabs ? window.getComputedStyle(tabs) : null;
      const verticalPadding = slideStyles
        ? parseFloat(slideStyles.paddingTop || 0) + parseFloat(slideStyles.paddingBottom || 0)
        : 0;
      const dialogInset = containerStyles
        ? parseFloat(containerStyles.paddingTop || 0) + parseFloat(containerStyles.paddingBottom || 0)
        : 0;
      const modalBorder = modalStyles
        ? parseFloat(modalStyles.borderTopWidth || 0) + parseFloat(modalStyles.borderBottomWidth || 0)
        : 0;
      const tabsSpacing = tabsStyles
        ? parseFloat(tabsStyles.marginTop || 0) + parseFloat(tabsStyles.marginBottom || 0)
        : 0;
      const modalChromeHeight = (header?.getBoundingClientRect().height || 0)
        + (tabs?.getBoundingClientRect().height || 0)
        + (footer?.getBoundingClientRect().height || 0)
        + tabsSpacing
        + modalBorder;
      const requestedHeight = Math.ceil(panel.getBoundingClientRect().height + verticalPadding + 3);
      const availableHeight = Math.max(220, Math.floor(window.innerHeight - dialogInset - modalChromeHeight));
      const nextHeight = Math.min(requestedHeight, availableHeight);
      setActivePanelHeight((currentHeight) => currentHeight === nextHeight ? currentHeight : nextHeight);
      setActivePanelScrollable(requestedHeight > availableHeight);
    };

    measurePanel();
    const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(measurePanel) : null;
    resizeObserver?.observe(panel);
    window.addEventListener('resize', measurePanel);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', measurePanel);
    };
  }, [accountLoading, activeTab, open]);

  const selectTab = (tabId) => {
    setActiveTab(tabId);
    setAlert(null);
  };

  const handlePersonalSubmit = async (event) => {
    event.preventDefault();
    if (!personalForm.name.trim()) {
      setAlert({ type: 'error', message: 'Ad alanı zorunludur.' });
      return;
    }
    if (!personalFormDirty || activeRequest === 'personal') return;

    const nextName = capitalizeName(personalForm.name);
    const payload = { userInfo, name: nextName, team: personalForm.team };
    setActiveRequest('personal');
    setAlert(null);
    try {
      const result = await invokeAction(onUpdateProfile, payload, 'Kişisel bilgiler güncellenemedi.');
      const nextProfile = { ...profile, name: nextName, team: personalForm.team };
      setProfile(nextProfile);
      setPersonalForm({ name: nextName, team: personalForm.team });
      setAlert({ type: 'success', message: previewMode && result?.preview ? 'Demo kişisel bilgileri güncellendi.' : 'Kişisel bilgiler başarıyla güncellendi.' });
      if (!previewMode) onUpdated?.({ type: 'profile', data: nextProfile, result });
    } catch (error) {
      setAlert({ type: 'error', message: error.message || 'Kişisel bilgiler güncellenemedi.' });
    } finally {
      setActiveRequest('');
    }
  };

  const validatePassword = () => {
    // Mevcut e-posta sunucuda doğrulanır (hatalı denemeler hesapta sayılır); burada yalnız boş olmadığına bakılır.
    if (!passwordForm.email.trim() || !passwordForm.currentPassword || !passwordForm.newPassword) return 'Tüm alanları doldurmanız gerekiyor.';
    if (!isCredentialValueAllowed(passwordForm.newPassword)) return PASSWORD_CHARACTER_ERROR;
    if (passwordForm.newPassword.length < 8) return 'Yeni şifre en az 8 karakter olmalıdır.';
    if (passwordForm.currentPassword === passwordForm.newPassword) return 'Yeni şifre mevcut şifrenizden farklı olmalıdır.';
    if (!passwordStrength.checks.noSequential) return 'Şifrenizde ardışık rakamlar bulunmamalıdır (ör. 123, 456).';
    if (!passwordStrength.checks.noRepeating) return 'Şifrenizde art arda tekrar eden karakterler bulunmamalıdır.';
    if (!passwordStrength.checks.notCommon || passwordStrength.score < 40) return 'Şifreniz çok zayıf. Daha güçlü bir şifre seçin.';
    return '';
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    const validationError = validatePassword();
    if (validationError) {
      setAlert({ type: 'error', message: validationError });
      return;
    }

    const payload = { userInfo, ...passwordForm, email: passwordForm.email.trim() };
    setActiveRequest('password');
    setAlert(null);
    try {
      const result = await invokeAction(onChangePassword, payload, 'Şifre değiştirilemedi.');
      const nextPassword = passwordForm.newPassword;
      setPasswordForm({ email: '', currentPassword: '', newPassword: '' });
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setAlert({
        type: 'success',
        message: previewMode && result?.preview
          ? 'Demo şifresi güncellendi.'
          : 'Şifreniz değiştirildi. IPTV kurulumlarınızdaki şifreyi de güncellemeniz gerekir.',
      });
      if (!previewMode) {
        onPasswordChanged?.(nextPassword, result);
        onUpdated?.({ type: 'password', result });
      }
    } catch (error) {
      setAlert({ type: 'error', message: error.message || 'Şifre değiştirilemedi.' });
    } finally {
      setActiveRequest('');
    }
  };

  // Adim 1: mevcut (kayitli) e-posta adresine 6 haneli dogrulama kodu gonder.
  const handleSendEmailCode = async () => {
    const newEmail = emailForm.newEmail.trim();
    if (!newEmail) {
      setAlert({ type: 'error', message: 'Yeni e-posta adresini girin.' });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      setAlert({ type: 'error', message: 'Geçerli bir e-posta adresi girin.' });
      return;
    }

    setActiveRequest('email-code');
    setAlert(null);
    try {
      await invokeAction(onSendEmailCode, { userInfo, newEmail }, 'Doğrulama kodu gönderilemedi.');
      setEmailCodeSent(true);
      setEmailCodeSeconds(180);
      setAlert({
        type: 'success',
        message: 'Mevcut e-posta adresinize doğrulama kodu gönderdik. Kod 3 dakika geçerlidir.',
      });
    } catch (error) {
      setAlert({ type: 'error', message: error.message || 'Doğrulama kodu gönderilemedi.' });
    } finally {
      setActiveRequest('');
    }
  };

  // Adim 2: kodu dogrula, e-postayi degistir.
  const handleEmailSubmit = async (event) => {
    event.preventDefault();
    if (!emailCodeSent) {
      await handleSendEmailCode();
      return;
    }

    const newEmail = emailForm.newEmail.trim();
    const code = emailForm.code.trim();
    if (code.length !== 6) {
      setAlert({ type: 'error', message: 'Doğrulama kodu 6 haneli olmalıdır.' });
      return;
    }

    const payload = { userInfo, newEmail, code };
    setActiveRequest('email');
    setAlert(null);
    try {
      const result = await invokeAction(onUpdateEmail, payload, 'E-posta adresi güncellenemedi.');
      const nextProfile = { ...profile, email: newEmail };
      setProfile(nextProfile);
      setEmailForm({ newEmail: '', code: '' });
      setEmailCodeSent(false);
      setEmailCodeSeconds(0);
      setAlert({ type: 'success', message: previewMode && result?.preview ? 'Demo e-posta adresi güncellendi.' : 'E-posta adresi başarıyla güncellendi.' });
      if (!previewMode) onUpdated?.({ type: 'email', data: nextProfile, result });
    } catch (error) {
      setAlert({ type: 'error', message: error.message || 'E-posta adresi güncellenemedi.' });
    } finally {
      setActiveRequest('');
    }
  };

  // Adim 1: hesaba kayitli e-posta adresine 6 haneli kod gonder.
  const handleSendPhoneCode = async () => {
    const newPhone = phoneForm.newPhone.trim();
    if (!newPhone) {
      setAlert({ type: 'error', message: 'Yeni telefon numarasını girin.' });
      return;
    }
    if (normalizePhone(newPhone).length < 10) {
      setAlert({ type: 'error', message: 'Geçerli bir telefon numarası girin.' });
      return;
    }

    setActiveRequest('phone-code');
    setAlert(null);
    try {
      await invokeAction(onSendPhoneCode, { userInfo, newPhone }, 'Doğrulama kodu gönderilemedi.');
      setPhoneCodeSent(true);
      setPhoneCodeSeconds(180);
      setAlert({ type: 'success', message: 'Doğrulama kodu e-posta adresinize gönderildi. Kod 3 dakika geçerlidir.' });
    } catch (error) {
      setAlert({ type: 'error', message: error.message || 'Doğrulama kodu gönderilemedi.' });
    } finally {
      setActiveRequest('');
    }
  };

  // Adim 2: kodu dogrula, numarayi degistir.
  const handlePhoneSubmit = async (event) => {
    event.preventDefault();
    if (!phoneCodeSent) {
      await handleSendPhoneCode();
      return;
    }

    const newPhone = phoneForm.newPhone.trim();
    const code = phoneForm.code.trim();
    if (code.length !== 6) {
      setAlert({ type: 'error', message: 'Doğrulama kodu 6 haneli olmalıdır.' });
      return;
    }

    const payload = { userInfo, newPhone, code };
    setActiveRequest('phone');
    setAlert(null);
    try {
      const result = await invokeAction(onUpdatePhone, payload, 'Telefon numarası güncellenemedi.');
      const nextProfile = { ...profile, phone: newPhone };
      setProfile(nextProfile);
      setPhoneForm({ newPhone: '', code: '' });
      setPhoneCodeSent(false);
      setPhoneCodeSeconds(0);
      setAlert({ type: 'success', message: previewMode && result?.preview ? 'Demo telefon numarası güncellendi.' : 'Telefon numarası başarıyla güncellendi.' });
      if (!previewMode) onUpdated?.({ type: 'phone', data: nextProfile, result });
    } catch (error) {
      setAlert({ type: 'error', message: error.message || 'Telefon numarası güncellenemedi.' });
    } finally {
      setActiveRequest('');
    }
  };

  const handleRemoveDevice = async (device) => {
    setRemovingDeviceId(device.id);
    setDevicesError('');
    try {
      if (!previewMode && typeof onRemoveTrustedDevice === 'function') {
        ensureSuccessfulResult(
          await onRemoveTrustedDevice({ userInfo, deviceId: device.id, device }),
          'Güvenilir cihaz kaldırılamadı.',
        );
      } else if (!previewMode) {
        throw new Error('Güvenilir cihaz kaldırma bağlantısı henüz tanımlanmadı.');
      }
      setTrustedDevices((current) => current.filter((item) => item.id !== device.id));
    } catch (error) {
      setDevicesError(error.message || 'Güvenilir cihaz kaldırılamadı.');
    } finally {
      setRemovingDeviceId(null);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth={false}
      keepMounted
      transitionDuration={0}
      disableEscapeKeyDown
      className="tvup-account-dialog"
      aria-labelledby="tvup-account-modal-title"
      aria-describedby="tvup-account-modal-description"
      slotProps={{
        backdrop: { className: 'tvup-account-backdrop' },
        paper: { id: 'tvup-account-modal', className: 'tvup-account-modal' },
      }}
    >
      <header className="tvup-account-header">
        <div className="tvup-account-title-icon" aria-hidden="true"><EditRoundedIcon /></div>
        <div className="tvup-account-title-copy">
          <h2 id="tvup-account-modal-title">Hesabı Düzenle</h2>
          <p id="tvup-account-modal-description"><span>{profile.username}</span> hesap bilgilerini yönetin.</p>
        </div>
        <button type="button" className="tvup-account-close" aria-label="Hesap düzenleme penceresini kapat" onClick={() => onClose?.()}>
          <CloseRoundedIcon />
        </button>
      </header>

      <TivigoToast notice={alert} />

      <div
        className="tvup-account-edit-tabs"
        role="tablist"
        aria-label="Hesap ayarları"
        style={{
          '--tvup-account-tab-x': accountTabIndicator.x,
          '--tvup-account-tab-width': accountTabIndicator.width,
        }}
      >
        {TABS.map((tab) => {
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`tvup-account-tab-${tab.id}`}
              aria-controls={`tvup-account-panel-${tab.id}`}
              aria-selected={activeTab === tab.id}
              aria-label={tab.label}
              className={activeTab === tab.id ? 'is-active' : ''}
              onClick={() => selectTab(tab.id)}
            >
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      <div className="tvup-account-body">
        {accountLoading ? (
          <div className="tvup-account-loading"><CircularProgress size={30} /><span>Hesap bilgileri hazırlanıyor...</span></div>
        ) : (
          <div
            className={`tvup-account-panel-window${activePanelScrollable ? ' is-scrollable' : ''}`}
            style={activePanelHeight ? { height: `${activePanelHeight}px` } : undefined}
          >
            <div
              className="tvup-account-panel-track"
              style={{ transform: `translate3d(-${activeTabIndex * 100}%, 0, 0)` }}
            >
              <div className="tvup-account-panel-slide" aria-hidden={activeTab !== 'personal'} inert={activeTab === 'personal' ? undefined : true}>
                <form ref={(node) => { panelRefs.current.personal = node; }} id="tvup-account-panel-personal" role="tabpanel" aria-labelledby="tvup-account-tab-personal" className="tvup-account-panel" onSubmit={handlePersonalSubmit} noValidate>
                <div className="tvup-account-panel-heading"><h3>Profilinizi güncelleyin</h3></div>
                <label className="tvup-account-edit-field"><span>Adınız</span><input type="text" value={personalForm.name} autoComplete="name" onChange={(event) => setPersonalForm((current) => ({ ...current, name: event.target.value }))} /></label>
                <div className="tvup-account-edit-field"><span id="tvup-account-team-label">Tuttuğunuz Takım</span><Autocomplete
                  className="tvup-account-team-select"
                  value={selectedTeam}
                  options={TEAMS}
                  open={teamSelectOpen}
                  onOpen={() => setTeamSelectOpen(true)}
                  onClose={() => setTeamSelectOpen(false)}
                  onChange={(_, team) => setPersonalForm((current) => ({ ...current, team: team?.value || '' }))}
                  getOptionLabel={(team) => team.label}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  popupIcon={<KeyboardArrowDownRoundedIcon />}
                  openOnFocus
                  autoHighlight
                  disableClearable
                  noOptionsText="Takım bulunamadı"
                  slotProps={{
                    popper: { className: 'tvup-account-team-popper' },
                    paper: { className: 'tvup-account-team-menu' },
                  }}
                  renderInput={(params) => <TextField {...params} autoComplete="off" inputProps={{ ...params.inputProps, 'aria-labelledby': 'tvup-account-team-label' }} />}
                /></div>
                </form>
              </div>

              <div className="tvup-account-panel-slide" aria-hidden={activeTab !== 'password'} inert={activeTab === 'password' ? undefined : true}>
                <form ref={(node) => { panelRefs.current.password = node; }} id="tvup-account-panel-password" role="tabpanel" aria-labelledby="tvup-account-tab-password" className="tvup-account-panel" onSubmit={handlePasswordSubmit} autoComplete="off" data-form-type="other" noValidate>
                <div className="tvup-account-panel-heading"><h3>Şifrenizi değiştirin</h3></div>
                <div className="tvup-account-verification-warning">Güvenliğiniz için, şifre değişikliği işlemini başlatmak üzere mevcut e-posta adresinizi eksiksiz girin.</div>
                <div className="tvup-account-current"><small>MEVCUT E-POSTA ADRESİNİZ</small><b>{previewMode ? profile.email : maskEmail(profile.email)}</b></div>
                <label className="tvup-account-edit-field"><span>Mevcut E-posta Adresiniz</span><input type="email" name="tvup-password-email-confirmation" value={passwordForm.email} autoComplete="off" data-lpignore="true" data-1p-ignore="true" data-form-type="other" onChange={(event) => setPasswordForm((current) => ({ ...current, email: event.target.value }))} /></label>
                <label className="tvup-account-edit-field"><span>Mevcut Şifre</span><div className="tvup-account-password-field"><input type={showCurrentPassword ? 'text' : 'password'} name="tvup-current-account-secret" value={passwordForm.currentPassword} autoComplete="new-password" data-lpignore="true" data-1p-ignore="true" data-form-type="other" onChange={(event) => setPasswordForm((current) => ({ ...current, currentPassword: event.target.value }))} /><button type="button" aria-label={showCurrentPassword ? 'Mevcut şifreyi gizle' : 'Mevcut şifreyi göster'} onClick={() => setShowCurrentPassword((current) => !current)}>{showCurrentPassword ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}</button></div></label>
                <label className={`tvup-account-edit-field${newPasswordInvalidChar ? ' is-error' : ''}`}>
                  <span>Yeni Şifre</span>
                  <div className={`tvup-account-password-field${newPasswordInvalidChar ? ' is-error' : ''}`}>
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      name="tvup-new-account-secret"
                      value={passwordForm.newPassword}
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      data-form-type="other"
                      aria-invalid={newPasswordInvalidChar}
                      onChange={(event) => setPasswordForm((current) => ({ ...current, newPassword: event.target.value }))}
                    />
                    <button type="button" aria-label={showNewPassword ? 'Yeni şifreyi gizle' : 'Yeni şifreyi göster'} onClick={() => setShowNewPassword((current) => !current)}>{showNewPassword ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}</button>
                  </div>
                </label>
                {passwordForm.newPassword && (
                  <div className={`tvup-account-strength${newPasswordInvalidChar ? ' is-error' : ''}`}>
                    <span><b>Şifre gücü</b><em>{passwordStrength.level}</em></span>
                    <div><i style={{ width: `${passwordStrength.score}%` }} /></div>
                    <small role={newPasswordInvalidChar ? 'alert' : undefined} aria-live="polite">
                      {newPasswordInvalidChar
                        ? PASSWORD_CHARACTER_ERROR
                        : 'En az 8 karakter; İngilizce harf, rakam ve alt tire kullanabilirsiniz.'}
                    </small>
                  </div>
                )}
                </form>
              </div>

              <div className="tvup-account-panel-slide" aria-hidden={activeTab !== 'email'} inert={activeTab === 'email' ? undefined : true}>
                <form ref={(node) => { panelRefs.current.email = node; }} id="tvup-account-panel-email" role="tabpanel" aria-labelledby="tvup-account-tab-email" className="tvup-account-panel" onSubmit={handleEmailSubmit} noValidate>
                <div className="tvup-account-panel-heading"><h3>E-posta adresinizi güncelleyin</h3></div>
                <div className="tvup-account-verification-warning">
                  {emailCodeSent
                    ? 'Mevcut e-posta adresinize gönderdiğimiz 6 haneli kodu girin.'
                    : 'Güvenliğiniz için, değişikliği onaylamak üzere mevcut e-posta adresinize 6 haneli bir kod göndereceğiz.'}
                </div>
                <div className="tvup-account-current"><small>MEVCUT E-POSTA ADRESİNİZ</small><b>{previewMode ? profile.email : maskEmail(profile.email)}</b></div>
                <label className="tvup-account-edit-field">
                  <span>Yeni E-posta Adresi</span>
                  <input
                    type="email"
                    value={emailForm.newEmail}
                    autoComplete="off"
                    readOnly={emailCodeSent}
                    onChange={(event) => setEmailForm((current) => ({ ...current, newEmail: event.target.value }))}
                  />
                </label>
                {emailCodeSent ? (
                  <>
                    <label className="tvup-account-edit-field">
                      <span>Doğrulama Kodu</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={emailForm.code}
                        autoComplete="one-time-code"
                        onChange={(event) => setEmailForm((current) => ({
                          ...current,
                          code: event.target.value.replace(/\D/g, '').slice(0, 6),
                        }))}
                      />
                    </label>
                    <div className="tvup-account-verification-warning">
                      {emailCodeSeconds > 0
                        ? `Kodun geçerlilik süresi: ${Math.floor(emailCodeSeconds / 60)}:${String(emailCodeSeconds % 60).padStart(2, '0')}`
                        : 'Kodun süresi doldu. Yeni kod isteyebilirsiniz.'}
                      {' '}
                      <button
                        type="button"
                        className="tvup-account-link-button"
                        disabled={emailCodeSeconds > 0 || activeRequest === 'email-code'}
                        onClick={handleSendEmailCode}
                      >
                        Yeni kod gönder
                      </button>
                    </div>
                  </>
                ) : null}
                </form>
              </div>

              <div className="tvup-account-panel-slide" aria-hidden={activeTab !== 'phone'} inert={activeTab === 'phone' ? undefined : true}>
                <form ref={(node) => { panelRefs.current.phone = node; }} id="tvup-account-panel-phone" role="tabpanel" aria-labelledby="tvup-account-tab-phone" className="tvup-account-panel" onSubmit={handlePhoneSubmit} noValidate>
                <div className="tvup-account-panel-heading"><h3>Telefon numaranızı güncelleyin</h3></div>
                <div className="tvup-account-verification-warning">
                  {phoneCodeSent
                    ? 'E-posta adresinize gönderdiğimiz 6 haneli kodu girin.'
                    : 'Güvenliğiniz için, değişikliği onaylamak üzere e-posta adresinize 6 haneli bir kod göndereceğiz.'}
                </div>
                <div className="tvup-account-current"><small>MEVCUT TELEFON NUMARANIZ</small><b>{previewMode ? profile.phone : maskPhone(profile.phone)}</b></div>
                <label className="tvup-account-edit-field">
                  <span>Yeni Telefon Numarası</span>
                  <input
                    type="tel"
                    value={phoneForm.newPhone}
                    autoComplete="off"
                    readOnly={phoneCodeSent}
                    onChange={(event) => setPhoneForm((current) => ({ ...current, newPhone: event.target.value }))}
                  />
                </label>
                {phoneCodeSent ? (
                  <>
                    <label className="tvup-account-edit-field">
                      <span>Doğrulama Kodu</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={phoneForm.code}
                        autoComplete="one-time-code"
                        onChange={(event) => setPhoneForm((current) => ({
                          ...current,
                          code: event.target.value.replace(/\D/g, '').slice(0, 6),
                        }))}
                      />
                    </label>
                    <div className="tvup-account-verification-warning">
                      {phoneCodeSeconds > 0
                        ? `Kodun geçerlilik süresi: ${Math.floor(phoneCodeSeconds / 60)}:${String(phoneCodeSeconds % 60).padStart(2, '0')}`
                        : 'Kodun süresi doldu. Yeni kod isteyebilirsiniz.'}
                      {' '}
                      <button
                        type="button"
                        className="tvup-account-link-button"
                        disabled={phoneCodeSeconds > 0 || activeRequest === 'phone-code'}
                        onClick={handleSendPhoneCode}
                      >
                        Yeni kod gönder
                      </button>
                    </div>
                  </>
                ) : null}
                </form>
              </div>

              <div className="tvup-account-panel-slide" aria-hidden={activeTab !== 'devices'} inert={activeTab === 'devices' ? undefined : true}>
                <section ref={(node) => { panelRefs.current.devices = node; }} id="tvup-account-panel-devices" role="tabpanel" aria-labelledby="tvup-account-tab-devices" className="tvup-account-panel tvup-account-devices">
                <div className="tvup-account-info-note">Bu bilgiler şifrelidir, sadece siz görebilirsiniz.</div>
                {loginsError && <div className="tvup-account-device-error" role="alert"><ErrorRoundedIcon />{loginsError}</div>}
                {loginsLoading ? <div className="tvup-account-devices-loading"><CircularProgress size={26} /><span>Oturum kayıtları alınıyor...</span></div> : loginHistory.length === 0 ? <div className="tvup-account-empty"><DevicesRoundedIcon /><b>Henüz oturum kaydı yok</b><span>Hesabınıza giriş yaptığınız cihazlar burada listelenir.</span></div> : <div className="tvup-account-device-list">{loginHistory.map((item) => <article key={item.id} className={`tvup-account-device${item.success === false ? ' is-failed' : ''}`}><span className="tvup-account-device-icon"><DevicesRoundedIcon /></span><div><b>{item.device || 'Bilinmeyen cihaz'}{item.success === false ? ' · Başarısız' : ''}</b><small>IP: {item.ip_address || 'Bilgi yok'}{item.location ? ` · ${item.location}` : ''}</small><em>{formatDate(item.login_at, true, true)}{item.success === false && item.failure_reason ? ` · ${item.failure_reason}` : ''}</em></div>{item.success !== false && (item.reported ? <span className="tvup-account-device-reported">Bildirildi</span> : <button type="button" className="tvup-account-device-report" disabled={reportingId !== null} onClick={() => setReportTarget(item)}>{reportingId === item.id ? <CircularProgress size={15} /> : <ShieldRoundedIcon />}<span>Bu ben değilim</span></button>)}</article>)}</div>}
                </section>
              </div>
            </div>
          </div>
        )}
      </div>

      {activeSubmitAction && (
        <footer className="tvup-account-footer">
          <button
            type="submit"
            form={`tvup-account-panel-${activeTab}`}
            className="tvup-account-submit"
            disabled={activeSubmitAction.disabled}
          >
            {activeSubmitAction.pending ? <><CircularProgress size={16} /> {activeSubmitAction.pendingLabel}</> : activeSubmitAction.label}
          </button>
        </footer>
      )}

      {/* Onay: islem geri alinamaz, kullanici her cihazdan duser. */}
      {reportTarget && (
        <div className="tvup-account-report-overlay" role="dialog" aria-modal="true">
          <div className="tvup-account-report-box">
            <span className="tvup-account-report-icon"><ShieldRoundedIcon /></span>
            <b>Bu giriş size ait değil mi?</b>
            <p>
              <strong>{reportTarget.device || 'Bilinmeyen cihaz'}</strong>
              {reportTarget.location ? ` · ${reportTarget.location}` : ''}
              <br />
              {reportTarget.ip_address} · {formatDate(reportTarget.login_at, true, true)}
            </p>
            <p className="tvup-account-report-warn">
              Onaylarsanız <strong>tüm cihazlardaki oturumlarınız kapatılır</strong> ve bu giriş
              ekibimizin incelemesine düşer. Yeniden giriş yaptıktan sonra
              <strong> şifrenizi hemen değiştirin</strong>.
            </p>
            <div className="tvup-account-report-actions">
              <button type="button" className="tvup-account-report-cancel" onClick={() => setReportTarget(null)} disabled={reportingId !== null}>
                Vazgeç
              </button>
              <button type="button" className="tvup-account-report-confirm" onClick={handleReportLogin} disabled={reportingId !== null}>
                {reportingId !== null ? <><CircularProgress size={15} /> Gönderiliyor</> : 'Evet, ben değilim'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sonuc: kullanici artik cikis yapmis durumda. */}
      {reportDone && (
        <div className="tvup-account-report-overlay" role="dialog" aria-modal="true">
          <div className="tvup-account-report-box">
            <span className="tvup-account-report-icon is-done"><ShieldRoundedIcon /></span>
            <b>Bildiriminiz alındı</b>
            <p>Tüm cihazlardaki oturumlarınız kapatıldı. Giriş bilgileriniz ekibimizin incelemesine düştü.</p>
            <p className="tvup-account-report-warn">
              <strong>Şifrenizi hemen değiştirin.</strong> Yeniden giriş yaptıktan sonra Hesabım &gt; Şifre
              bölümünden yeni ve daha önce kullanmadığınız bir şifre belirleyin.
            </p>
            <div className="tvup-account-report-actions">
              <button type="button" className="tvup-account-report-confirm" onClick={() => { setReportDone(false); onClose?.(); }}>
                Anladım
              </button>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}

export default UserPanelAccountModal;
